import { useEffect, useMemo, useState } from 'react';
import DoctorCard, { type DoctorCardData } from '@/components/feature/DoctorCard';

interface DoctorDirectoryProps {
  items: DoctorCardData[];
}

const levelOf = (title: string) => {
  if (title.startsWith('副主任医师')) return '副主任医师';
  if (title.startsWith('主任医师')) return '主任医师';
  return '其他';
};

export default function DoctorDirectory({ items }: DoctorDirectoryProps) {
  const [keyword, setKeyword] = useState('');
  const [level, setLevel] = useState('all');
  const [specialty, setSpecialty] = useState('all');
  const pageSize = 8;
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const levels = useMemo(
    () => Array.from(new Set(items.map((item) => levelOf(item.title)))),
    [items],
  );

  const specialties = useMemo(
    () => Array.from(new Set(items.flatMap((item) => item.tags))),
    [items],
  );

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return items.filter((item) => {
      const matchLevel = level === 'all' || levelOf(item.title) === level;
      const matchSpecialty =
        specialty === 'all' || item.tags.includes(specialty);
      const matchKeyword =
        !kw ||
        item.name.toLowerCase().includes(kw) ||
        item.hospital.toLowerCase().includes(kw) ||
        item.intro.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchLevel && matchSpecialty && matchKeyword;
    });
  }, [items, keyword, level, specialty]);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [keyword, level, specialty]);

  const visibleDoctors = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  const remaining = filtered.length - visibleCount;

  const resetFilters = () => {
    setKeyword('');
    setLevel('all');
    setSpecialty('all');
  };

  return (
    <section className="w-full px-4 md:px-8 py-12 md:py-16">
      <div className="max-w-7xl mx-auto">
        {/* 筛选栏 */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative w-full sm:w-64">
                <i className="ri-search-2-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索医师姓名、医院或擅长"
                  className="w-full pl-9 pr-9 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                />
                {keyword.trim() && (
                  <button
                    type="button"
                    onClick={() => setKeyword('')}
                    aria-label="清除搜索"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-200/60 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-base"></i>
                  </button>
                )}
              </div>

              {levels.length > 1 && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setLevel('all')}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      level === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    全部职称
                  </button>
                  {levels.map((lv) => (
                    <button
                      key={lv}
                      type="button"
                      onClick={() => setLevel(lv)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        level === lv
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      {lv}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <i className="ri-filter-3-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none"></i>
                <select
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  aria-label="擅长领域筛选"
                  className="appearance-none pl-9 pr-8 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
                >
                  <option value="all">全部擅长领域</option>
                  {specialties.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none"></i>
              </div>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                共 <span className="font-semibold text-primary-600">{filtered.length}</span> 位医师
              </span>
            </div>
          </div>
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {visibleDoctors.map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>

            {hasMore && (
              <div className="flex flex-col items-center mt-10">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + pageSize)}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-md border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-base"></i>
                  加载更多医师
                </button>
                <span className="mt-3 text-xs text-foreground-500">
                  已展示 {visibleCount} / {filtered.length} 位，还有 {remaining} 位
                </span>
              </div>
            )}

            {!hasMore && filtered.length > pageSize && (
              <div className="flex items-center justify-center gap-2 mt-10 text-xs text-foreground-400">
                <i className="ri-checkbox-circle-line text-base"></i>
                已经到底啦，全部 {filtered.length} 位医师都在这里了
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg border border-background-200 bg-background-50">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-user-search-line text-2xl"></i>
            </div>
            <p className="text-foreground-800 font-medium">没有找到匹配的医师</p>
            <p className="mt-1.5 text-sm text-foreground-500">换个关键词、职称或擅长领域试试。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}